import { useCallback, useState } from 'react'

import { externalAuthApi } from '../api'
import { DEFAULT_ERROR_MESSAGE } from '../constants'
import { clearEntityNoticePhone, readEntityNoticePhone } from '../utils/entity-notice-phone-storage'
import { clearSignupDataCookie } from '../utils/signup-data-cookie'
import { useAuthBase } from './useAuthBase'

export interface UseEntityNoticeFinalizeSignupOptions {
  token: string
  onSuccess: () => void
}

interface EntityNoticeCpfSubmission {
  cpf: string
  birthdate: string
  phone?: string
  whatsAppOptIn?: boolean
}

interface UseEntityNoticeFinalizeSignupReturn {
  isLoading: boolean
  error: string | undefined
  setError: (error: string | undefined) => void
  submitCpf: (data: EntityNoticeCpfSubmission) => Promise<void>
}

/**
 * "Finalize seu cadastro" step of the entity-notice fake-door signup (cells
 * A/B): converts the CPF form's ISO birthdate to the timestamp
 * `completeSignupWithCpf` expects (the same conversion `SignupCpfForm`'s own
 * internal submit does), calls it directly via `SignupCpfForm`'s `onSubmit`
 * escape hatch, and advances in-modal on success — no phone step, no
 * `mustRedirectTo` follow, no page navigation. `name`/`password`/`occupation`
 * travel via the `signup_data` cookie `CompleteDataForm`'s step already set
 * (same mechanism the standalone CPF-based signup page relies on); this hook
 * only clears it once the account is confirmed created.
 *
 * Phone/whatsAppOptIn can arrive two ways: typed on this very step's own
 * "Telefone (opcional)" field (passed in directly), or captured earlier on
 * the entry screen's WhatsApp field (cell A) — persisted to localStorage by
 * `EntityNoticeEntry` since a page reload sits between the two steps. The
 * explicit submission wins if both are present; either way the server call
 * carries whichever value is available.
 */
export function useEntityNoticeFinalizeSignup(
  options: UseEntityNoticeFinalizeSignupOptions
): UseEntityNoticeFinalizeSignupReturn {
  const { token, onSuccess } = options
  const { error, setError, handleAsyncOperation } = useAuthBase()
  const [isLoading, setIsLoading] = useState(false)

  const submitCpf = useCallback(
    async ({ cpf, birthdate, phone, whatsAppOptIn }: EntityNoticeCpfSubmission) => {
      setIsLoading(true)
      setError(undefined)

      const storedPhone = phone === undefined ? readEntityNoticePhone() : ''
      const resolvedPhone = phone ?? (storedPhone || undefined)
      const resolvedWhatsAppOptIn = whatsAppOptIn ?? (storedPhone ? true : undefined)

      const birthDate = new Date(`${birthdate}T00:00:00-03:00`).getTime()
      const result = await handleAsyncOperation(async () =>
        externalAuthApi.completeSignupWithCpf({
          token,
          cpf,
          birthDate,
          phone: resolvedPhone,
          whatsAppOptIn: resolvedWhatsAppOptIn,
        })
      )

      if (!result) {
        setIsLoading(false)
        return
      }

      const { response, data } = result

      if (!response.ok) {
        setError(data?.message || DEFAULT_ERROR_MESSAGE)
        setIsLoading(false)
        return
      }

      clearSignupDataCookie()
      clearEntityNoticePhone()
      setIsLoading(false)
      onSuccess()
    },
    [token, onSuccess, handleAsyncOperation, setError]
  )

  return {
    isLoading,
    error,
    setError,
    submitCpf,
  }
}
